/** Normalize text for case-insensitive search (collapse whitespace). */
export function normalizeSearchText(text: string): string {
  return (text || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function isSubsequence(needle: string, haystack: string): boolean {
  if (!needle) return true;
  let i = 0;
  for (const ch of haystack) {
    if (ch === needle[i]) {
      i += 1;
      if (i === needle.length) return true;
    }
  }
  return false;
}

/** Length of smallest span in haystack covering needle chars in order. */
function fuzzySpanLength(needle: string, haystack: string): number {
  let start = -1;
  let end = -1;
  let i = 0;
  for (let j = 0; j < haystack.length && i < needle.length; j++) {
    if (haystack[j] === needle[i]) {
      if (start < 0) start = j;
      end = j;
      i += 1;
    }
  }
  return i === needle.length ? end - start + 1 : Number.POSITIVE_INFINITY;
}

/**
 * Score how well `text` matches `query`. Higher = closer match. 0 = no match.
 */
export function scoreSearchText(query: string, text: string): number {
  const q = normalizeSearchText(query);
  const hay = normalizeSearchText(text);
  if (!q || !hay) return 0;

  if (hay === q) return 1000;

  if (hay.startsWith(q)) {
    return 850 + Math.min(80, Math.round((q.length / hay.length) * 80));
  }

  const words = hay.split(" ");
  for (const word of words) {
    if (word.startsWith(q)) {
      return 720 + Math.min(40, Math.round((q.length / word.length) * 40));
    }
  }

  const idx = hay.indexOf(q);
  if (idx >= 0) {
    return 520 + Math.max(0, 40 - Math.min(idx, 40));
  }

  if (q.length >= 2 && isSubsequence(q, hay)) {
    const span = fuzzySpanLength(q, hay);
    if (Number.isFinite(span)) {
      return 320 + Math.max(0, 80 - span);
    }
  }

  const tokens = q.split(" ").filter(Boolean);
  if (tokens.length > 1) {
    const scores = tokens.map((t) => scoreSearchText(t, hay));
    if (scores.every((s) => s > 0)) {
      const min = Math.min(...scores);
      const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
      return min * 0.65 + avg * 0.35;
    }
  }

  return 0;
}

/** Best score across multiple searchable fields on one item. */
export function scoreSearchFields(
  query: string,
  fields: (string | undefined | null)[],
): number {
  let best = 0;
  for (const field of fields) {
    if (!field?.trim()) continue;
    best = Math.max(best, scoreSearchText(query, field));
  }
  return best;
}

export type RankedSearchResult<T> = {
  item: T;
  score: number;
};

/** Rank items by relevance to query (highest score first). Empty query returns all items unscored. */
export function rankSearchResults<T>(
  items: T[],
  query: string,
  getFields: (item: T) => (string | undefined | null)[],
  minScore = 1,
): RankedSearchResult<T>[] {
  const q = normalizeSearchText(query);
  if (!q) {
    return items.map((item) => ({ item, score: 0 }));
  }

  return items
    .map((item) => ({
      item,
      score: scoreSearchFields(q, getFields(item)),
    }))
    .filter((r) => r.score >= minScore)
    .sort((a, b) => b.score - a.score);
}

/** Filter + sort by relevance (closest match first). */
export function filterBySmartSearch<T>(
  items: T[],
  query: string,
  getFields: (item: T) => (string | undefined | null)[],
): T[] {
  return rankSearchResults(items, query, getFields).map((r) => r.item);
}

export type SearchSuggestion = {
  id: string;
  label: string;
  subtitle?: string;
  /** Value written into the search field when selected */
  value?: string;
};

/** Build ranked suggestion list for autocomplete dropdowns. */
export function buildSearchSuggestions<T>(
  items: T[],
  query: string,
  options: {
    getFields: (item: T) => (string | undefined | null)[];
    getId: (item: T) => string;
    getLabel: (item: T) => string;
    getSubtitle?: (item: T) => string | undefined;
    getValue?: (item: T) => string;
    limit?: number;
    minChars?: number;
  },
): SearchSuggestion[] {
  const minChars = options.minChars ?? 1;
  if (normalizeSearchText(query).length < minChars) return [];

  return rankSearchResults(items, query, options.getFields)
    .slice(0, options.limit ?? 8)
    .map(({ item }) => ({
      id: options.getId(item),
      label: options.getLabel(item),
      subtitle: options.getSubtitle?.(item),
      value: options.getValue?.(item),
    }));
}
